import styled from 'styled-components';

import { FormBuilder } from '@components/form-builder';
import { defaultFormBuilderFields } from '@components/form-builder/form-builder';
import { FormV2 } from '@components/form/v2';
import { Spinner } from '@components/loading-spinner';
import { useOverlay } from '@components/overlay';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  useCreateJourneyActionFormFile,
  useCreateJourneyActionFormImage,
  useJourneyActionForm,
  useUpdateJourneyActionForm,
} from 'api/journey-action-forms/hooks';
import { useJourneyAction } from 'api/journey-actions/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';
import { ActionVariable } from 'api/types';
import { useVariableGroups } from 'ui/journey-builder/node-types/common';

type EditJourneyActionFormModalProps = {
  formId: string;
  openPreview?: boolean;
  onClose?: () => void;
  actionVariables?: ActionVariable[];
};

export const useEditJourneyActionFormOverlay = () => {
  return useOverlay(EditJourneyActionFormModal);
};

const EditJourneyActionFormModal = ({
  formId,
  openPreview,
  onClose,
  actionVariables: actionVariablesProp,
}: EditJourneyActionFormModalProps) => {
  const formQuery = useJourneyActionForm(formId);
  const journeyActionId = formQuery.data?.journeyActionId ?? '';
  const shouldFetchActionVariables = !actionVariablesProp && Boolean(journeyActionId);
  const journeyActionQuery = useJourneyAction(journeyActionId, {
    enabled: shouldFetchActionVariables,
  });
  const { data: lists, isLoading: listsLoading } = useFormBuilderLists();
  const actionVariables = actionVariablesProp ?? journeyActionQuery.data?.actionVariables;
  const updateForm = useUpdateJourneyActionForm(
    formId,
    formQuery.data?.journeyActionId,
    formQuery.data?.journey?.shortId
  );
  const createFormImageQuery = useCreateJourneyActionFormImage();
  const createFormFileQuery = useCreateJourneyActionFormFile();

  const journeyId = formQuery.data?.journey?.shortId ?? '';
  const baseVariableGroups = useVariableGroups({
    actionVariables,
    journeyId,
    excludedTypes: ['file', 'file_multi'],
  });

  const isLoadingActionVariables = shouldFetchActionVariables && journeyActionQuery.isLoading;
  if (formQuery.isLoading || listsLoading || isLoadingActionVariables) {
    return <Spinner />;
  }

  if (!formQuery.isSuccess) {
    return <Spinner />;
  }

  const form = formQuery.data;

  return (
    <Container>
      <FormBuilder
        mode={openPreview ? 'preview' : 'edit'}
        fields={defaultFormBuilderFields}
        name={form.name}
        description={form.description || ''}
        form={form.fieldsConfig}
        submissionMessage={form.submissionMessageConfig}
        predefinedOptionsLists={lists}
        variableGroups={baseVariableGroups}
        onSave={async (template, onSaveSuccess) => {
          const response = await updateForm.mutateAsync({
            ...template,
          });
          onSaveSuccess?.(
            response.fieldsConfig as FormV2,
            response.submissionMessageConfig as FormV2
          );
        }}
        onImageSave={async (props) => {
          const response = await createFormImageQuery.mutateAsync(props);
          return response.imageId;
        }}
        onFileSave={async (props) => {
          return createFormFileQuery.mutateAsync(props);
        }}
        onComplete={onClose}
        useTabs
      />
    </Container>
  );
};

const Container = styled(Stack)({
  position: 'fixed',
  top: pxToRem(24),
  left: pxToRem(24),
  right: pxToRem(24),
  bottom: pxToRem(24),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
});
